import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { api, formatErr, mediaUrl } from "@/lib/api";

function Shell({ title, sub, children, testid }) {
  const { settings } = useSite();
  return (
    <div className="container-bt flex min-h-[75vh] items-center justify-center py-14" data-testid={testid}>
      <div className="w-full max-w-md">
        <img src={mediaUrl(settings.brand.logo_url)} alt="" className="mx-auto mb-8 h-14" />
        <h1 className="text-center text-4xl">{title}</h1>
        {sub && <p className="mt-3 text-center">{sub}</p>}
        <div className="mt-10">{children}</div>
      </div>
    </div>
  );
}

const Err = ({ msg }) => (msg ? <p className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="auth-error">{msg}</p> : null);

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const u = await login(f.email, f.password);
      nav(params.get("next") || (u.role === "admin" ? "/admin" : "/account"));
    } catch (e2) {
      setErr(formatErr(e2));
      setLoading(false);
    }
  };
  return (
    <Shell title="Welcome back" sub="Sign in to continue your growth." testid="login-page">
      <form onSubmit={submit} className="space-y-4">
        <input className="input-bt" type="email" placeholder="Email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" />
        <input className="input-bt" type="password" placeholder="Password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" />
        <Err msg={err} />
        <button className="btn-primary w-full" disabled={loading} data-testid="login-submit-button">{loading ? "Signing in..." : "Sign in"}</button>
      </form>
      <div className="mt-6 flex justify-between text-sm">
        <Link to="/forgot-password" className="underline" data-testid="forgot-password-link">Forgot password?</Link>
        <Link to={`/register${params.get("next") ? `?next=${params.get("next")}` : ""}`} className="font-semibold text-bt-primary underline" data-testid="go-register-link">Create an account</Link>
      </div>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ first_name: "", email: "", password: "", marketing_consent: false });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await register(f);
      nav(params.get("next") || "/account");
    } catch (e2) {
      setErr(formatErr(e2));
      setLoading(false);
    }
  };
  return (
    <Shell title="Start growing" sub="Create your free Better Together account." testid="register-page">
      <form onSubmit={submit} className="space-y-4">
        <input className="input-bt" placeholder="First name" required value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="register-first-name-input" />
        <input className="input-bt" type="email" placeholder="Email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="register-email-input" />
        <input className="input-bt" type="password" placeholder="Password (6+ characters)" minLength={6} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="register-password-input" />
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" className="mt-0.5 h-5 w-5 accent-[color:var(--bt-primary)]" checked={f.marketing_consent} onChange={(e) => setF({ ...f, marketing_consent: e.target.checked })} data-testid="register-consent-checkbox" />
          Send me Better Together growth tools by email. Unsubscribe anytime.
        </label>
        <Err msg={err} />
        <button className="btn-primary w-full" disabled={loading} data-testid="register-submit-button">{loading ? "Creating..." : "Create account"}</button>
      </form>
      <p className="mt-6 text-center text-sm">Already have an account? <Link to="/login" className="font-semibold text-bt-primary underline" data-testid="go-login-link">Sign in</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/auth/forgot-password", { email }).catch(() => {});
    setDone(true);
  };
  return (
    <Shell title="Reset password" sub="We'll send you a link to set a new password." testid="forgot-page">
      {done ? <p className="rounded-2xl bg-bt-sage p-5 text-center" data-testid="forgot-success">If that email has an account, a reset link is on its way.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <input className="input-bt" type="email" placeholder="Email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          <button className="btn-primary w-full" data-testid="forgot-submit-button">Send link</button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: params.get("token"), password: pw });
      nav("/login");
    } catch (e2) {
      setErr(formatErr(e2));
    }
  };
  return (
    <Shell title="Choose a new password" testid="reset-page">
      <form onSubmit={submit} className="space-y-4">
        <input className="input-bt" type="password" minLength={6} placeholder="New password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
        <Err msg={err} />
        <button className="btn-primary w-full" data-testid="reset-submit-button">Save password</button>
      </form>
    </Shell>
  );
}
